import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { opportunitiesService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';
import { supabase } from '../../../lib/supabase';

const STATUS_OPTIONS = ['Identified', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const SECTOR_OPTIONS = ['Healthcare', 'Financial Services', 'Technology', 'Retail & E-Commerce', 'Manufacturing', 'Energy & Utilities', 'Telecommunications', 'Education', 'Government & Public Sector', 'Real Estate', 'Transportation & Logistics', 'Media & Entertainment', 'Professional Services', 'Non-Profit', 'Other'];
const ROLE_OPTIONS = ['Lead', 'Co-Lead', 'Support', 'Technical Advisor', 'Commercial', 'Delivery', 'Bid Manager'];

const STATUS_COLORS = {
  identified: 'bg-slate-100 text-slate-600',
  qualified: 'bg-primary/10 text-primary',
  proposal: 'bg-warning/10 text-warning',
  negotiation: 'bg-accent/10 text-accent',
  won: 'bg-success/10 text-success',
  lost: 'bg-error/10 text-error',
};

const OpportunityDetailDrawer = ({ opportunity, onClose, onUpdated, partners = [] }) => {
  const toast = useToast();
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'delete'
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: opportunity?.name || '',
    partner_id: opportunity?.partner_id || '',
    value: opportunity?.rawValue || '',
    opp_status: opportunity?.status || 'Identified',
    win_probability: opportunity?.winProbability || 0,
    expected_close: opportunity?.rawExpectedClose || '',
    sector: opportunity?.sector || '',
    role: opportunity?.role || '',
    description: opportunity?.description || '',
    owned_by: opportunity?.ownedBy || 'eCity',
  });

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Opportunity title is required.'); return; }
    setSaving(true);
    try {
      await opportunitiesService?.update(opportunity?.id, {
        name: form?.name,
        partner_id: form?.partner_id || null,
        value: parseFloat(form?.value) || 0,
        opp_status: form?.opp_status,
        win_probability: parseInt(form?.win_probability) || 0,
        expected_close: form?.expected_close || null,
        sector: form?.sector,
        role: form?.role,
        description: form?.description,
        owned_by: form?.owned_by,
      });
      toast?.success('Opportunity updated successfully');
      onUpdated?.();
      setMode('view');
    } catch (err) {
      toast?.error(err?.message || 'Failed to update opportunity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase?.from('opportunities')?.delete()?.eq('id', opportunity?.id);
      if (error) throw error;
      toast?.success('Opportunity deleted');
      onUpdated?.();
      onClose();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete opportunity');
    } finally {
      setDeleting(false);
    }
  };

  if (!opportunity) return null;

  const statusKey = (form?.opp_status || opportunity?.status || '')?.toLowerCase();

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full z-50 w-full max-w-[520px] bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-secondary/5 to-primary/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center flex-shrink-0">
              <Icon name="Target" size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500 font-medium">Joint Opportunity</p>
              <h2 className="text-base font-bold text-slate-900 leading-tight truncate max-w-[300px]">{opportunity?.name}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {mode === 'view' && (
              <>
                <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                  <Icon name="Pencil" size={13} /> Edit
                </button>
                <button onClick={() => setMode('delete')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-error/10 text-error text-xs font-semibold hover:bg-error/20 transition-colors">
                  <Icon name="Trash2" size={13} /> Delete
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Delete Confirmation */}
        {mode === 'delete' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-error/10 flex items-center justify-center mb-4">
              <Icon name="Trash2" size={28} className="text-error" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Opportunity?</h3>
            <p className="text-sm text-slate-500 mb-2">You are about to permanently delete:</p>
            <p className="text-sm font-semibold text-slate-800 mb-6 px-4 py-2 bg-slate-50 rounded-xl">{opportunity?.name}</p>
            <p className="text-xs text-slate-400 mb-8">This action cannot be undone. All associated data will be removed.</p>
            <div className="flex gap-3 w-full max-w-xs">
              <button onClick={() => setMode('view')} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 rounded-xl bg-error text-white text-sm font-semibold hover:bg-error/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Deleting...</> : <><Icon name="Trash2" size={14} /> Delete</>}
              </button>
            </div>
          </div>
        )}

        {/* View Mode */}
        {mode === 'view' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${STATUS_COLORS?.[statusKey] || 'bg-slate-100 text-slate-600'}`}>
                {opportunity?.status}
              </span>
              {opportunity?.role && (
                <span className="px-3 py-1 rounded-lg text-xs font-semibold bg-secondary/10 text-secondary">{opportunity?.role}</span>
              )}
              {opportunity?.ownedBy && (
                <span className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${opportunity?.ownedBy === 'eCity' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                  {opportunity?.ownedBy === 'eCity' ? '🏢' : '🤝'} {opportunity?.ownedBy}-Led
                </span>
              )}
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'Building2', label: 'Sector', value: opportunity?.sector || '—' },
                { icon: 'Banknote', label: 'Value', value: opportunity?.value || '—' },
                { icon: 'Calendar', label: 'Expected Close', value: opportunity?.expectedClose || 'TBD' },
                { icon: 'TrendingUp', label: 'Win Probability', value: `${opportunity?.winProbability || 0}%` },
              ]?.map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon name={item?.icon} size={13} className="text-slate-400" />
                    <span className="text-xs text-slate-500 font-medium">{item?.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{item?.value}</p>
                </div>
              ))}
            </div>

            {/* Win Probability Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-600">Win Probability</span>
                <span className="text-sm font-bold text-primary">{opportunity?.winProbability}%</span>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-secondary to-primary rounded-full transition-all" style={{ width: `${opportunity?.winProbability}%` }} />
              </div>
            </div>

            {/* Partner */}
            {opportunity?.partnerName && (
              <div className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl">
                <Icon name="Handshake" size={15} className="text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 font-medium">Partner</p>
                  <p className="text-sm font-semibold text-slate-800">{opportunity?.partnerName}</p>
                </div>
              </div>
            )}

            {/* Description */}
            {opportunity?.description && (
              <div className="p-3.5 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium mb-1.5 flex items-center gap-1.5"><Icon name="FileText" size={13} />Description</p>
                <p className="text-sm text-slate-700 leading-relaxed">{opportunity?.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Edit Mode */}
        {mode === 'edit' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Owned By */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Owned By</label>
              <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50 p-1 gap-1">
                {['eCity', 'Partner']?.map(opt => (
                  <button key={opt} type="button" onClick={() => setForm(p => ({ ...p, owned_by: opt }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${form?.owned_by === opt ? (opt === 'eCity' ? 'bg-blue-600 text-white shadow-sm' : 'bg-purple-600 text-white shadow-sm') : 'text-slate-500 hover:text-slate-700 hover:bg-white'}`}>
                    <span>{opt === 'eCity' ? '🏢' : '🤝'}</span> {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Opportunity Title <span className="text-red-400">*</span></label>
              <input name="name" value={form?.name} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all" />
            </div>

            {/* Partner */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Partner</label>
              <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                <option value="">— No Partner —</option>
                {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
              </select>
            </div>

            {/* Value + Stage */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Value (OMR)</label>
                <input name="value" type="number" min="0" value={form?.value} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Stage</label>
                <select name="opp_status" value={form?.opp_status} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                  {STATUS_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Win % + Expected Close */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Win Probability (%)</label>
                <input name="win_probability" type="number" min="0" max="100" value={form?.win_probability} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Expected Close</label>
                <input name="expected_close" type="date" value={form?.expected_close} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
            </div>

            {/* Sector + Role */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Sector</label>
                <select name="sector" value={form?.sector} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                  <option value="">Select sector...</option>
                  {SECTOR_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Engagement Role</label>
                <select name="role" value={form?.role} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                  <option value="">Select role...</option>
                  {ROLE_OPTIONS?.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Description</label>
              <textarea name="description" value={form?.description} onChange={handleChange} rows={3} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none transition-all" />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 bg-slate-50">
          {mode === 'view' && (
            <button onClick={onClose} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors">
              Close
            </button>
          )}
          {mode === 'edit' && (
            <div className="flex gap-3">
              <button onClick={() => setMode('view')} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-secondary to-primary text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={14} /> Save Changes</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OpportunityDetailDrawer;
